import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { StorageService } from '../services/storage.service';
import { AuthStateService } from '../services/auth-state.service';
import { TenantService } from '../services/tenant.service';
import { DialogService } from '../services/dialog.service';
import { isSessionInvalidError } from './session.interceptor';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const storageService = inject(StorageService);
  const authStateService = inject(AuthStateService);
  const tenantService = inject(TenantService);
  const dialogService = inject(DialogService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Session invalidation is handled by sessionInterceptor (dialog + redirect on OK).
      // This interceptor runs before sessionInterceptor on errors, so skip 401 session expiry here.
      if (error.status === 401 && (isSessionInvalidError(error) || dialogService.isSessionExpirationHandling())) {
        return throwError(() => error);
      }

      if (error.status === 401) {
        // Unauthorized - get tenant code before clearing
        const tenantCode = storageService.getSessionStorage('tenant_code');
        
        // Clear all session storage items
        storageService.clearSessionStorage();
        // Clear `dm_email` / `_grecaptcha`
        storageService.clearLoggedOutBrowserKeys();
        authStateService.logout();
        
        // Navigate to tenant-specific sign-in page (no dialog for 401)
        if (tenantCode) {
          // Get tenant to find short name for URL
          firstValueFrom(tenantService.getTenantByCode(tenantCode)).then(tenant => {
            if (tenant?.tenantShortName) {
              router.navigate([`/${tenant.tenantShortName}`]);
            } else {
              router.navigate(['/page-not-found']);
            }
          }).catch(() => {
            router.navigate(['/page-not-found']);
          });
        } else {
          router.navigate(['/page-not-found']);
        }
      }
      
      return throwError(() => error);
    })
  );
};
